// @givmo/ai — the §2.3 forward-compat decode rule (the crux — a CLIENT RUNTIME
// rule, NOT schema validation). Mirrors iOS `UiBlock.init(from:)`
// (`AssistantUIBlock.swift`) and the cross-platform "every platform renders every
// fixture" discipline.
//
// Render a block only when BOTH the `type` is KNOWN and the `block_version` is
// `<= SUPPORTED_BLOCK_VERSION`. Otherwise degrade — NEVER throw (a frozen-binary
// crash-safety guarantee; the consumer must keep rendering the rest of the turn):
//   - a NON-`text` block carries a REQUIRED `fallback_text` (schema-guaranteed)
//     and degrades to `{ kind: 'unsupported', fallback_text }`;
//   - a `text` block has no `fallback_text`; a higher-than-supported `text`
//     `block_version` degrades to `{ kind: 'degraded_text', text }` showing the
//     raw `text` verbatim.
//
// This lets the backend ship a new block type / bumped version to all clients at
// once; older clients degrade to readable copy instead of dropping the content or
// crashing. The renderer registry folds both degrade cases into a plain text
// bubble (mirroring iOS `RenderBlock.init(uiBlock:)`).
import {
  SUPPORTED_BLOCK_VERSION,
  type ActionChipsBlock,
  type CharityCardsBlock,
  type ComparisonBlock,
  type CrisisBlock,
  type DisclosureBlock,
  type EscalationOfferBlock,
  type KnownUiBlock,
  type TextBlock,
} from './blocks';

/** A block this client could not render natively (unknown `type`, or a known
 * non-`text` type at an unsupported-higher `block_version`). Renders its
 * schema-guaranteed `fallback_text` verbatim. Mirrors iOS `.unsupported`. */
export interface UnsupportedBlock {
  kind: 'unsupported';
  /** The unrecognized/over-version `type` (diagnostic; never trusted for render). */
  unsupportedType: string;
  fallback_text: string;
}

/** A `text` block at an unsupported-higher `block_version`. Renders its raw `text`
 * verbatim (the `text` block is its own degrade path). Mirrors iOS `.degradedText`. */
export interface DegradedTextBlock {
  kind: 'degraded_text';
  text: string;
}

/** A known v1 block, tagged for the renderer registry. The `block` keeps its exact
 * wire shape (discriminated on `block.type`). */
export interface SupportedBlock {
  kind: 'supported';
  block: KnownUiBlock;
}

/** The decode result for a single ui_block: either a supported known block or one
 * of the two §2.3 degrade cases. */
export type DecodedUiBlock = SupportedBlock | UnsupportedBlock | DegradedTextBlock;

/** Just enough of the common envelope to drive the discriminator + forward-compat
 * decision before trusting the concrete payload. */
interface BlockEnvelope {
  type?: unknown;
  block_version?: unknown;
  fallback_text?: unknown;
  text?: unknown;
}

function asString(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

/**
 * Decode one wire ui_block into a `DecodedUiBlock`, applying the §2.3 rule. Never
 * throws — a malformed or future block degrades to readable copy.
 */
export function decodeUiBlock(raw: unknown): DecodedUiBlock {
  const envelope = (raw ?? {}) as BlockEnvelope;
  const type = asString(envelope.type);
  // A non-integer / missing `block_version` is treated as version 0 → supported
  // (the schema requires an integer; a missing one is a malformed-but-tolerable
  // payload we still try to render its known type for).
  const blockVersion =
    typeof envelope.block_version === 'number' && Number.isFinite(envelope.block_version)
      ? envelope.block_version
      : 0;
  const versionSupported = blockVersion <= SUPPORTED_BLOCK_VERSION;

  // Known `text`: its own degrade path.
  if (type === 'text') {
    if (versionSupported) {
      return { kind: 'supported', block: raw as TextBlock };
    }
    return { kind: 'degraded_text', text: asString(envelope.text) };
  }

  const degrade = (): UnsupportedBlock => ({
    kind: 'unsupported',
    unsupportedType: type,
    fallback_text: asString(envelope.fallback_text),
  });

  // Every other known type: only when the version is supported; else degrade to
  // the required fallback_text. An unknown type also degrades.
  if (!versionSupported) {
    return degrade();
  }

  switch (type) {
    case 'action_chips':
      return { kind: 'supported', block: raw as ActionChipsBlock };
    case 'charity_cards':
      return { kind: 'supported', block: raw as CharityCardsBlock };
    case 'comparison':
      return { kind: 'supported', block: raw as ComparisonBlock };
    case 'escalation_offer':
      return { kind: 'supported', block: raw as EscalationOfferBlock };
    case 'crisis':
      return { kind: 'supported', block: raw as CrisisBlock };
    case 'disclosure':
      return { kind: 'supported', block: raw as DisclosureBlock };
    default:
      // Unknown type → degrade to the required fallback_text.
      return degrade();
  }
}

/** Decode an ordered list of wire ui_blocks (e.g. `message_complete.ui_blocks`). */
export function decodeUiBlocks(raw: readonly unknown[] | null | undefined): DecodedUiBlock[] {
  if (!raw) return [];
  return raw.map(decodeUiBlock);
}
